import { useState, useEffect } from 'react';
import api, { getErrorMessage } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/utils';
import {
  Shield, Building2, Users, Search, Eye, Power, CheckCircle, Loader2, Calendar, Clock,
  AlertTriangle, MapPin, Plus, Pencil, Trash2, Key, UserPlus, UserMinus, CreditCard,
  LayoutDashboard, FileText, RefreshCw, Filter, ChevronRight, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const superAdminAPI = {
  listCompanies:      ()                        => api.get('/companies'),
  getCompanyUsers:    (companyId)               => api.get('/users', { params: { company_id: companyId } }),
  getAllUsers:        ()                        => api.get('/users'),
  getCompanyObjects:  (companyId)               => api.get(`/companies/${companyId}/objects`),
  getCompanyStats:    (companyId)               => api.get(`/companies/${companyId}/stats`),
  toggleStatus:       (companyId, isActive)     => api.put(`/companies/${companyId}/status`, null, { params: { is_active: isActive } }),
  createCompany:      (data)                    => api.post('/companies', null, { params: data }),
  updateCompany:      (companyId, data)         => api.put(`/companies/${companyId}`, null, { params: data }),
  updateSubscription: (companyId, data)         => api.put(`/companies/${companyId}/subscription`, null, { params: data }),
  deleteCompany:      (companyId)               => api.delete(`/companies/${companyId}`, { params: { confirm: true } }),
  addUser:            (companyId, data)         => api.post(`/companies/${companyId}/users`, null, { params: data }),
  removeUser:         (companyId, userId)       => api.delete(`/companies/${companyId}/users/${userId}`),
  deleteUser:         (userId)                  => api.delete(`/users/${userId}`),
  resetPassword:      (companyId, userId, pwd)  => api.post(`/companies/${companyId}/users/${userId}/reset-password`, null, { params: { new_password: pwd } }),
  getAudit:           (params)                  => api.get('/audit', { params }),
  deleteObject:       (objectId)                => api.delete(`/objects/${objectId}`),
};

const STATUS_COLORS = {
  active:   'bg-emerald-500/20 text-emerald-400',
  trial:    'bg-amber-500/20 text-amber-400',
  past_due: 'bg-rose-500/20 text-rose-400',
  canceled: 'bg-slate-500/20 text-slate-400',
  expired:  'bg-slate-500/20 text-slate-400',
};

const ROLE_COLORS = {
  admin:       'bg-rose-500/20 text-rose-400',
  hr:          'bg-purple-500/20 text-purple-400',
  controller:  'bg-amber-500/20 text-amber-400',
  employee:    'bg-emerald-500/20 text-emerald-400',
  super_admin: 'bg-violet-500/20 text-violet-400',
};

export default function SuperAdmin() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab]                 = useState('dashboard');
  const [loading, setLoading]                     = useState(true);
  const [companies, setCompanies]                 = useState([]);
  const [allUsers, setAllUsers]                   = useState([]);
  const [auditLogs, setAuditLogs]                 = useState([]);
  const [auditLoading, setAuditLoading]           = useState(false);
  const [usersLoading, setUsersLoading]           = useState(false);
  const [searchQuery, setSearchQuery]             = useState('');
  const [usersSearch, setUsersSearch]             = useState('');
  const [usersCompanyFilter, setUsersCompanyFilter] = useState('all');
  const [selectedCompany, setSelectedCompany]     = useState(null);
  const [companyUsers, setCompanyUsers]           = useState([]);
  const [companyObjects, setCompanyObjects]       = useState([]);
  const [companyStats, setCompanyStats]           = useState(null);
  const [loadingDetails, setLoadingDetails]       = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [togglingStatus, setTogglingStatus]       = useState(null);
  const [createDialogOpen, setCreateDialogOpen]   = useState(false);
  const [editDialogOpen, setEditDialogOpen]       = useState(false);
  const [subDialogOpen, setSubDialogOpen]         = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen]   = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [resetPwdDialogOpen, setResetPwdDialogOpen] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser]           = useState(null);
  const [actionLoading, setActionLoading]         = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', email: '', phone: '', address: '',
    admin_name: '', admin_email: '', admin_password: '',
    max_employees: 10, subscription_plan: 'trial',
  });
  const [editForm, setEditForm]           = useState({ name: '', email: '', phone: '', address: '' });
  const [subForm, setSubForm]             = useState({ subscription_plan: '', subscription_status: '', max_employees: '', trial_days: '' });
  const [addUserForm, setAddUserForm]     = useState({ name: '', email: '', password: '', role: 'employee' });
  const [newPassword, setNewPassword]     = useState('');
  const [auditLimit, setAuditLimit]       = useState(100);

  const tabs = [
    { id: 'dashboard', label: t('superAdmin.tabDashboard') || 'Dashboard',   icon: LayoutDashboard },
    { id: 'companies', label: t('superAdmin.tabCompanies') || t('superAdmin.title') || 'Companies', icon: Building2 },
    { id: 'employees', label: t('superAdmin.tabEmployees') || t('nav.employees') || 'Employees',   icon: Users },
    { id: 'audit',     label: t('superAdmin.tabAudit')     || 'Audit',        icon: FileText },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCompanies(); }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'employees' && allUsers.length === 0) loadAllUsers();
    if (activeTab === 'audit'     && auditLogs.length === 0) loadAudit();
  }, [activeTab]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const res = await superAdminAPI.listCompanies();
      setCompanies(res.data);
    } catch (e) {
      toast.error(t('superAdmin.loadError') || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      setUsersLoading(true);
      const res = await superAdminAPI.getAllUsers();
      setAllUsers(res.data);
    } catch (e) {
      toast.error(t('msg.failedLoad') || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAudit = async () => {
    try {
      setAuditLoading(true);
      const res = await superAdminAPI.getAudit({ limit: auditLimit });
      setAuditLogs(res.data);
    } catch (e) {
      toast.error(t('msg.failedLoad') || 'Failed to load audit');
    } finally {
      setAuditLoading(false);
    }
  };

  const loadCompanyDetails = async (company) => {
    setSelectedCompany(company);
    setDetailsDialogOpen(true);
    setLoadingDetails(true);
    setCompanyUsers([]); setCompanyObjects([]); setCompanyStats(null);
    try {
      const [ur, or, sr] = await Promise.all([
        superAdminAPI.getCompanyUsers(company.id),
        superAdminAPI.getCompanyObjects(company.id),
        superAdminAPI.getCompanyStats(company.id),
      ]);
      setCompanyUsers(ur.data);
      setCompanyObjects(or.data);
      setCompanyStats(sr.data);
    } catch (e) {
      toast.error(t('msg.failedLoad'));
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleCompanyStatus = async (company, newStatus) => {
    setTogglingStatus(company.id);
    try {
      await superAdminAPI.toggleStatus(company.id, newStatus);
      toast.success(newStatus ? t('superAdmin.companyActivated') : t('superAdmin.companyDeactivated'));
      loadCompanies();
    } catch (e) {
      toast.error(t('msg.failedUpdate'));
    } finally {
      setTogglingStatus(null);
    }
  };

  const handleCreateCompany = async () => {
    setActionLoading(true);
    try {
      await superAdminAPI.createCompany(createForm);
      toast.success(t('superAdmin.companyCreated'));
      setCreateDialogOpen(false);
      setCreateForm({ name:'',email:'',phone:'',address:'',admin_name:'',admin_email:'',admin_password:'',max_employees:10,subscription_plan:'trial' });
      loadCompanies();
    } catch (e) {
      toast.error(getErrorMessage(e, t('msg.failedCreate'), t));
    } finally { setActionLoading(false); }
  };

  const handleEditCompany = async () => {
    setActionLoading(true);
    try {
      await superAdminAPI.updateCompany(selectedCompany.id, editForm);
      toast.success(t('superAdmin.companyUpdated'));
      setEditDialogOpen(false);
      loadCompanies();
    } catch (e) {
      toast.error(getErrorMessage(e, t('msg.failedUpdate'), t));
    } finally { setActionLoading(false); }
  };

  const handleUpdateSubscription = async () => {
    setActionLoading(true);
    try {
      const params = {};
      if (subForm.subscription_plan)   params.subscription_plan   = subForm.subscription_plan;
      if (subForm.subscription_status) params.subscription_status = subForm.subscription_status;
      if (subForm.max_employees)       params.max_employees        = parseInt(subForm.max_employees);
      if (subForm.trial_days)          params.trial_days           = parseInt(subForm.trial_days);
      await superAdminAPI.updateSubscription(selectedCompany.id, params);
      toast.success(t('superAdmin.subscriptionUpdated'));
      setSubDialogOpen(false);
      loadCompanies();
    } catch (e) {
      toast.error(getErrorMessage(e, t('msg.failedUpdate'), t));
    } finally { setActionLoading(false); }
  };

  const handleDeleteCompany = async () => {
    setActionLoading(true);
    try {
      await superAdminAPI.deleteCompany(selectedCompany.id);
      toast.success(t('superAdmin.companyDeleted'));
      setDeleteDialogOpen(false);
      setDetailsDialogOpen(false);
      loadCompanies();
    } catch (e) {
      toast.error(getErrorMessage(e, t('msg.failedDelete'), t));
    } finally { setActionLoading(false); }
  };

  const handleAddUser = async () => {
    setActionLoading(true);
    try {
      await superAdminAPI.addUser(selectedCompany.id, addUserForm);
      toast.success(t('superAdmin.userAdded'));
      setAddUserDialogOpen(false);
      if (detailsDialogOpen) loadCompanyDetails(selectedCompany);
      loadCompanies();
      if (activeTab === 'employees') loadAllUsers();
    } catch (e) {
      toast.error(getErrorMessage(e, t('msg.failedCreate'), t));
    } finally { setActionLoading(false); }
  };

  const handleRemoveUser = async (user) => {
    try {
      await superAdminAPI.removeUser(selectedCompany.id, user.id);
      toast.success(t('superAdmin.userRemoved'));
      loadCompanyDetails(selectedCompany);
      loadCompanies();
    } catch (e) {
      toast.error(getErrorMessage(e, t('msg.failedDelete'), t));
    }
  };

  const handleDeleteUserConfirm = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await superAdminAPI.deleteUser(selectedUser.id);
      toast.success(t('superAdmin.userRemoved') || 'User deleted');
      setDeleteUserDialogOpen(false);
      loadAllUsers();
      loadCompanies();
    } catch (e) {
      toast.error(getErrorMessage(e, t('msg.failedDelete'), t));
    } finally { setActionLoading(false); }
  };

  const handleResetPassword = async () => {
    setActionLoading(true);
    try {
      const cId = selectedUser.company_id || selectedCompany?.id;
      await superAdminAPI.resetPassword(cId, selectedUser.id, newPassword);
      toast.success(t('superAdmin.passwordReset'));
      setResetPwdDialogOpen(false);
    } catch (e) {
      toast.error(getErrorMessage(e, t('msg.failedUpdate'), t));
    } finally { setActionLoading(false); }
  };

  const handleDeleteObject = async (obj) => {
    if (!window.confirm(`${t('common.delete')} "${obj.name}"?`)) return;
    try {
      await superAdminAPI.deleteObject(obj.id);
      toast.success(t('msg.deleted') || 'Object deleted');
      loadCompanyDetails(selectedCompany);
    } catch (e) {
      toast.error(getErrorMessage(e, t('msg.failedDelete'), t));
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = allUsers.filter(u => {
    const matchSearch = !usersSearch ||
      u.name?.toLowerCase().includes(usersSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(usersSearch.toLowerCase());
    const matchCompany = usersCompanyFilter === 'all' || u.company_id === usersCompanyFilter;
    return matchSearch && matchCompany;
  });

  const stats = {
    total:          companies.length,
    active:         companies.filter(c => c.subscription_status === 'active').length,
    trial:          companies.filter(c => c.subscription_status === 'trial').length,
    totalEmployees: companies.reduce((s, c) => s + (c.employee_count || 0), 0),
  };

  const getCompanyName = (companyId) => {
    const c = companies.find(c => c.id === companyId);
    return c?.name || companyId || '—';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-10 w-10 animate-spin text-wp-light" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-page">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white font-heading flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
            <Shield className="h-5 w-5 text-rose-400" />
          </div>
          {t('superAdmin.title')}
        </h1>
        {activeTab === 'companies' && (
          <Button onClick={() => setCreateDialogOpen(true)} className="bg-wp-medium hover:bg-wp-light text-white gap-2">
            <Plus className="h-4 w-4" />
            {t('superAdmin.createCompany')}
          </Button>
        )}
        {activeTab === 'audit' && (
          <Button onClick={loadAudit} variant="ghost" className="text-wp-light/70 gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('common.refresh') || 'Refresh'}
          </Button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-wp-dark/40 rounded-xl p-1">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-wp-medium text-white shadow'
                  : 'text-wp-light/50 hover:text-wp-light hover:bg-wp-dark/50'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ==================== DASHBOARD TAB ==================== */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: t('superAdmin.totalCompanies'),  value: stats.total,          icon: Building2,    color: 'text-wp-light',    bg: 'bg-wp-medium/20' },
              { label: t('superAdmin.activeCompanies'), value: stats.active,         icon: CheckCircle,  color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
              { label: t('superAdmin.trialCompanies'),  value: stats.trial,          icon: Calendar,     color: 'text-amber-400',   bg: 'bg-amber-500/20' },
              { label: t('superAdmin.totalEmployees'),  value: stats.totalEmployees, icon: Users,        color: 'text-wp-light',    bg: 'bg-wp-light/10' },
            ].map(({ label, value, icon: Icon, color, bg }) => (
              <div key={label} className="glass-card rounded-2xl p-5">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${color}`} />
                  </div>
                  <div>
                    <p className="text-wp-light/40 text-xs">{label}</p>
                    <p className="text-2xl font-bold text-white">{value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-wp-dark/50">
              <h3 className="text-white font-medium">{t('superAdmin.tabCompanies')}</h3>
              <button onClick={() => setActiveTab('companies')} className="text-wp-light/50 hover:text-wp-light text-sm flex items-center gap-1 transition-colors">
                {t('common.viewAll') || 'View all'} <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-wp-dark/30">
                  <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.company')}</th>
                  <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.status')}</th>
                  <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.employees')}</th>
                </tr>
              </thead>
              <tbody>
                {companies.slice(0, 6).map(c => (
                  <tr key={c.id} className="border-b border-wp-dark/20 hover:bg-wp-dark/20 transition-colors">
                    <td className="py-3 px-6 text-white font-medium">{c.name}</td>
                    <td className="py-3 px-6">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[c.subscription_status] || 'bg-slate-500/20 text-slate-400'}`}>
                        {c.subscription_status}
                      </span>
                    </td>
                    <td className="py-3 px-6 text-wp-light/60 text-sm">{c.employee_count || 0} / {c.max_employees}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================== COMPANIES TAB ==================== */}
      {activeTab === 'companies' && (
        <div className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('superAdmin.searchPlaceholder')}
              className="pl-11 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30"
            />
          </div>

          {filteredCompanies.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center">
              <Building2 className="h-12 w-12 mx-auto text-white/20 mb-4" />
              <h3 className="text-lg font-medium text-white">{t('superAdmin.noCompanies')}</h3>
            </div>
          ) : (
            <div className="glass-card rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-wp-dark/50">
                    <th className="text-left py-4 px-6 text-wp-light/50 text-sm font-medium">{t('superAdmin.company')}</th>
                    <th className="text-left py-4 px-6 text-wp-light/50 text-sm font-medium">{t('common.email')}</th>
                    <th className="text-left py-4 px-6 text-wp-light/50 text-sm font-medium">{t('superAdmin.subscription')}</th>
                    <th className="text-left py-4 px-6 text-wp-light/50 text-sm font-medium">{t('superAdmin.status')}</th>
                    <th className="text-left py-4 px-6 text-wp-light/50 text-sm font-medium">{t('superAdmin.employees')}</th>
                    <th className="text-left py-4 px-6 text-wp-light/50 text-sm font-medium">{t('superAdmin.created')}</th>
                    <th className="text-right py-4 px-6 text-wp-light/50 text-sm font-medium">{t('superAdmin.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.map(company => (
                    <tr key={company.id} className="border-b border-wp-dark/30 hover:bg-wp-dark/20 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${company.is_active ? 'bg-wp-medium/20' : 'bg-slate-500/20'}`}>
                            <Building2 className={`h-4 w-4 ${company.is_active ? 'text-wp-light' : 'text-slate-400'}`} />
                          </div>
                          <span className="text-white font-medium">{company.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-wp-light/60 text-sm">{company.email}</td>
                      <td className="py-4 px-6">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-wp-dark/50 text-white capitalize">{company.subscription_plan}</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[company.subscription_status] || 'bg-slate-500/20 text-slate-400'}`}>
                          {company.subscription_status}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-white text-sm">
                        {company.employee_count || 0} <span className="text-white/30">/ {company.max_employees}</span>
                      </td>
                      <td className="py-4 px-6 text-wp-light/40 text-sm">{formatDateTime(company.created_at)}</td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => loadCompanyDetails(company)} className="p-2 hover:bg-wp-dark/50 rounded-lg text-wp-light/40 hover:text-white transition-colors" title={t('superAdmin.viewDetails')}>
                            <Eye className="h-4 w-4" />
                          </button>
                          <button onClick={() => { setSelectedCompany(company); setEditForm({ name: company.name||'', email: company.email||'', phone: company.phone||'', address: company.address||'' }); setEditDialogOpen(true); }} className="p-2 hover:bg-wp-dark/50 rounded-lg text-wp-light/40 hover:text-white transition-colors" title={t('superAdmin.editCompany')}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => { setSelectedCompany(company); setSubForm({ subscription_plan: company.subscription_plan||'trial', subscription_status: company.subscription_status||'trial', max_employees: company.max_employees||10, trial_days: '' }); setSubDialogOpen(true); }} className="p-2 hover:bg-wp-dark/50 rounded-lg text-wp-light/40 hover:text-white transition-colors" title={t('superAdmin.editSubscription')}>
                            <CreditCard className="h-4 w-4" />
                          </button>
                          <button onClick={() => toggleCompanyStatus(company, !company.is_active)} disabled={togglingStatus === company.id} className={`p-2 hover:bg-wp-dark/50 rounded-lg transition-colors ${company.is_active ? 'text-amber-400' : 'text-emerald-400'}`} title={company.is_active ? t('superAdmin.deactivate') : t('superAdmin.activate')}>
                            {togglingStatus === company.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
                          </button>
                          <button onClick={() => { setSelectedCompany(company); setDeleteDialogOpen(true); }} className="p-2 hover:bg-wp-dark/50 rounded-lg text-rose-400 hover:text-rose-300 transition-colors" title={t('superAdmin.deleteCompany')}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ==================== EMPLOYEES TAB ==================== */}
      {activeTab === 'employees' && (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <Input
                value={usersSearch}
                onChange={(e) => setUsersSearch(e.target.value)}
                placeholder={t('superAdmin.searchPlaceholder') || 'Search...'}
                className="pl-11 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30"
              />
            </div>
            <Select value={usersCompanyFilter} onValueChange={setUsersCompanyFilter}>
              <SelectTrigger className="w-52 bg-wp-dark/30 border-wp-dark/50 text-white">
                <Filter className="h-4 w-4 mr-2 text-white/40" />
                <SelectValue placeholder={t('superAdmin.allCompanies') || 'All companies'} />
              </SelectTrigger>
              <SelectContent className="bg-wp-darker border-wp-dark/50">
                <SelectItem value="all">{t('superAdmin.allCompanies') || 'All companies'}</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={loadAllUsers} variant="ghost" className="text-wp-light/60 gap-2">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {usersLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-wp-light" /></div>
          ) : (
            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="px-6 py-3 border-b border-wp-dark/50 flex items-center justify-between">
                <span className="text-wp-light/50 text-sm">{filteredUsers.length} {t('superAdmin.employees').toLowerCase()}</span>
              </div>
              {filteredUsers.length === 0 ? (
                <div className="py-12 text-center text-wp-light/40"><Users className="h-10 w-10 mx-auto mb-3 opacity-30" />{t('superAdmin.noUsers')}</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-wp-dark/30">
                      <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.userName') || 'Name'}</th>
                      <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('common.email')}</th>
                      <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.selectRole') || 'Role'}</th>
                      <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.company')}</th>
                      <th className="text-right py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id} className="border-b border-wp-dark/20 hover:bg-wp-dark/20 transition-colors">
                        <td className="py-3 px-6 text-white font-medium">{user.name}</td>
                        <td className="py-3 px-6 text-wp-light/60 text-sm">{user.email}</td>
                        <td className="py-3 px-6">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[user.role] || 'bg-slate-500/20 text-slate-400'}`}>
                            {t(`role.${user.role}`) || user.role}
                          </span>
                        </td>
                        <td className="py-3 px-6 text-wp-light/50 text-sm">{getCompanyName(user.company_id)}</td>
                        <td className="py-3 px-6">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => { setSelectedUser(user); setNewPassword(''); setResetPwdDialogOpen(true); }}
                              className="p-2 hover:bg-wp-dark/50 rounded-lg text-wp-light/40 hover:text-white transition-colors"
                              title={t('superAdmin.resetPassword')}
                            >
                              <Key className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => { setSelectedUser(user); setDeleteUserDialogOpen(true); }}
                              className="p-2 hover:bg-wp-dark/50 rounded-lg text-rose-400 hover:text-rose-300 transition-colors"
                              title={t('common.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== AUDIT TAB ==================== */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Select value={String(auditLimit)} onValueChange={(v) => { setAuditLimit(Number(v)); }}>
              <SelectTrigger className="w-40 bg-wp-dark/30 border-wp-dark/50 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wp-darker border-wp-dark/50">
                <SelectItem value="50">50 {t('superAdmin.records') || 'records'}</SelectItem>
                <SelectItem value="100">100 {t('superAdmin.records') || 'records'}</SelectItem>
                <SelectItem value="250">250 {t('superAdmin.records') || 'records'}</SelectItem>
                <SelectItem value="500">500 {t('superAdmin.records') || 'records'}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={loadAudit} disabled={auditLoading} className="bg-wp-medium hover:bg-wp-light text-white gap-2">
              {auditLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {t('common.refresh') || 'Refresh'}
            </Button>
          </div>

          {auditLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-wp-light" /></div>
          ) : (
            <div className="glass-card rounded-2xl overflow-hidden">
              {auditLogs.length === 0 ? (
                <div className="py-12 text-center text-wp-light/40">
                  <Activity className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>{t('superAdmin.noAudit') || 'No audit records'}</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-wp-dark/50">
                      <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.auditWho') || 'Who'}</th>
                      <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.auditAction') || 'Action'}</th>
                      <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.auditDetails') || 'Details'}</th>
                      <th className="text-left py-3 px-6 text-wp-light/40 text-xs font-medium">{t('superAdmin.auditWhen') || 'When'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map(log => (
                      <tr key={log.id} className="border-b border-wp-dark/20 hover:bg-wp-dark/20 transition-colors">
                        <td className="py-3 px-6 text-white text-sm font-medium">{log.user_name}</td>
                        <td className="py-3 px-6">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-wp-medium/20 text-wp-light capitalize">
                            {log.action?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-6 text-wp-light/50 text-sm max-w-xs truncate">{log.details || '—'}</td>
                        <td className="py-3 px-6 text-wp-light/40 text-xs whitespace-nowrap">{formatDateTime(log.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== DIALOGS ==================== */}

      {/* Company Details */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-4xl bg-wp-darker border-wp-dark/50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Building2 className="h-5 w-5 text-wp-light" />
              {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription className="text-wp-light/50">{selectedCompany?.email}</DialogDescription>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-wp-light" /></div>
          ) : selectedCompany && (
            <div className="space-y-6">
              {companyStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: t('superAdmin.objects'),               value: companyStats.objects_count,        icon: MapPin,       color: 'text-wp-light' },
                    { label: t('superAdmin.employees'),             value: companyStats.total_users,          icon: Users,        color: 'text-wp-light' },
                    { label: t('superAdmin.workHoursThisMonth'),    value: `${companyStats.work_hours_this_month}h`, icon: Clock, color: 'text-emerald-400' },
                    { label: t('superAdmin.violationsThisMonth'),   value: companyStats.violations_this_month, icon: AlertTriangle, color: 'text-rose-400' },
                  ].map(({ label, value, icon: Icon, color }) => (
                    <div key={label} className="glass-card rounded-xl p-4 text-center">
                      <Icon className={`h-5 w-5 mx-auto mb-1 ${color}`} />
                      <p className={`text-xl font-bold ${color}`}>{value}</p>
                      <p className="text-wp-light/40 text-xs mt-1">{label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Users */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <Users className="h-4 w-4 text-wp-light" />
                    {t('superAdmin.companyUsers')} ({companyUsers.length})
                  </h4>
                  <Button size="sm" onClick={() => openAddUserDialog(selectedCompany)} className="bg-wp-medium/50 hover:bg-wp-medium text-white gap-1">
                    <UserPlus className="h-4 w-4" />
                    {t('superAdmin.addUser')}
                  </Button>
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {companyUsers.map(user => (
                    <div key={user.id} className="flex items-center justify-between p-3 rounded-xl bg-wp-dark/30">
                      <div>
                        <p className="text-white font-medium text-sm">{user.name}</p>
                        <p className="text-wp-light/40 text-xs">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[user.role] || 'bg-slate-500/20 text-slate-400'}`}>
                          {t(`role.${user.role}`) || user.role}
                        </span>
                        <button onClick={() => { setSelectedUser(user); setNewPassword(''); setResetPwdDialogOpen(true); }} className="p-1.5 hover:bg-wp-dark/50 rounded-lg text-wp-light/40 hover:text-white transition-colors"><Key className="h-4 w-4" /></button>
                        <button onClick={() => handleRemoveUser(user)} className="p-1.5 hover:bg-wp-dark/50 rounded-lg text-rose-400 hover:text-rose-300 transition-colors"><UserMinus className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Objects */}
              {companyObjects.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-wp-light" />
                    {t('superAdmin.objects')} ({companyObjects.length})
                  </h4>
                  <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                    {companyObjects.map(obj => (
                      <div key={obj.id} className="flex items-center justify-between p-3 rounded-xl bg-wp-dark/30">
                        <div>
                          <p className="text-white font-medium text-sm">{obj.name}</p>
                          <p className="text-wp-light/40 text-xs">{obj.address || '—'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${obj.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                            {obj.is_active ? t('common.active') : t('common.inactive')}
                          </span>
                          <button onClick={() => handleDeleteObject(obj)} className="p-1.5 hover:bg-wp-dark/50 rounded-lg text-rose-400 hover:text-rose-300 transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Company */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg bg-wp-darker border-wp-dark/50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Plus className="h-5 w-5 text-wp-light" />
              {t('superAdmin.createCompany')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-wp-light/70">{t('superAdmin.companyName')}</Label>
                <Input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-wp-light/70">{t('superAdmin.companyEmail')}</Label>
                <Input type="email" value={createForm.email} onChange={e => setCreateForm({...createForm, email: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-wp-light/70">{t('superAdmin.phone')}</Label>
                <Input value={createForm.phone} onChange={e => setCreateForm({...createForm, phone: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-wp-light/70">{t('superAdmin.address')}</Label>
                <Input value={createForm.address} onChange={e => setCreateForm({...createForm, address: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
              </div>
            </div>
            <div className="border-t border-wp-dark/50 pt-4 space-y-3">
              <p className="text-white font-medium text-sm">{t('superAdmin.adminData')}</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-wp-light/70">{t('superAdmin.adminName')}</Label>
                  <Input value={createForm.admin_name} onChange={e => setCreateForm({...createForm, admin_name: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-wp-light/70">{t('superAdmin.adminEmail')}</Label>
                  <Input type="email" value={createForm.admin_email} onChange={e => setCreateForm({...createForm, admin_email: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-wp-light/70">{t('superAdmin.adminPassword')}</Label>
                <Input type="password" value={createForm.admin_password} onChange={e => setCreateForm({...createForm, admin_password: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-wp-light/70">{t('superAdmin.plan')}</Label>
                <Select value={createForm.subscription_plan} onValueChange={v => setCreateForm({...createForm, subscription_plan: v})}>
                  <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-wp-darker border-wp-dark/50">
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-wp-light/70">{t('superAdmin.maxEmployees')}</Label>
                <Input type="number" value={createForm.max_employees} onChange={e => setCreateForm({...createForm, max_employees: parseInt(e.target.value)||10})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateDialogOpen(false)} className="text-wp-light/70">{t('common.cancel')}</Button>
            <Button onClick={handleCreateCompany} disabled={actionLoading || !createForm.name || !createForm.email || !createForm.admin_email || !createForm.admin_password} className="bg-wp-medium hover:bg-wp-light text-white">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md bg-wp-darker border-wp-dark/50">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Pencil className="h-5 w-5 text-wp-light" />
              {t('superAdmin.editCompany')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {[['name', t('superAdmin.companyName')], ['email', t('common.email')], ['phone', t('superAdmin.phone')], ['address', t('superAdmin.address')]].map(([field, label]) => (
              <div key={field} className="space-y-2">
                <Label className="text-wp-light/70">{label}</Label>
                <Input value={editForm[field]} onChange={e => setEditForm({...editForm, [field]: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" type={field === 'email' ? 'email' : 'text'} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-wp-light/70">{t('common.cancel')}</Button>
            <Button onClick={handleEditCompany} disabled={actionLoading} className="bg-wp-medium hover:bg-wp-light text-white">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription */}
      <Dialog open={subDialogOpen} onOpenChange={setSubDialogOpen}>
        <DialogContent className="max-w-md bg-wp-darker border-wp-dark/50">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-wp-light" />
              {t('superAdmin.editSubscription')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('superAdmin.plan')}</Label>
              <Select value={subForm.subscription_plan} onValueChange={v => setSubForm({...subForm, subscription_plan: v})}>
                <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-wp-darker border-wp-dark/50">
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('superAdmin.status')}</Label>
              <Select value={subForm.subscription_status} onValueChange={v => setSubForm({...subForm, subscription_status: v})}>
                <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-wp-darker border-wp-dark/50">
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('superAdmin.maxEmployees')}</Label>
              <Input type="number" value={subForm.max_employees} onChange={e => setSubForm({...subForm, max_employees: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('superAdmin.extendTrial')} ({t('superAdmin.trialDays')})</Label>
              <Input type="number" placeholder="30" value={subForm.trial_days} onChange={e => setSubForm({...subForm, trial_days: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubDialogOpen(false)} className="text-wp-light/70">{t('common.cancel')}</Button>
            <Button onClick={handleUpdateSubscription} disabled={actionLoading} className="bg-wp-medium hover:bg-wp-light text-white">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Company */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md bg-wp-darker border-wp-dark/50">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2 text-rose-400">
              <Trash2 className="h-5 w-5" />
              {t('superAdmin.deleteCompany')}
            </DialogTitle>
            <DialogDescription className="text-wp-light/70">{t('superAdmin.confirmDelete')}</DialogDescription>
          </DialogHeader>
          <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20">
            <p className="text-white font-medium">{selectedCompany?.name}</p>
            <p className="text-wp-light/50 text-sm">{selectedCompany?.email}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)} className="text-wp-light/70">{t('common.cancel')}</Button>
            <Button onClick={handleDeleteCompany} disabled={actionLoading} className="bg-rose-500 hover:bg-rose-600 text-white">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User */}
      <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
        <DialogContent className="max-w-md bg-wp-darker border-wp-dark/50">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-wp-light" />
              {t('superAdmin.addUser')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {[['name', t('superAdmin.userName'), 'text'], ['email', t('superAdmin.userEmail'), 'email'], ['password', t('auth.password'), 'password']].map(([field, label, type]) => (
              <div key={field} className="space-y-2">
                <Label className="text-wp-light/70">{label}</Label>
                <Input type={type} value={addUserForm[field]} onChange={e => setAddUserForm({...addUserForm, [field]: e.target.value})} className="bg-wp-dark/30 border-wp-dark/50 text-white" />
              </div>
            ))}
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('superAdmin.selectRole')}</Label>
              <Select value={addUserForm.role} onValueChange={v => setAddUserForm({...addUserForm, role: v})}>
                <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-wp-darker border-wp-dark/50">
                  <SelectItem value="admin">{t('role.admin')}</SelectItem>
                  <SelectItem value="hr">{t('role.hr')}</SelectItem>
                  <SelectItem value="controller">{t('role.controller')}</SelectItem>
                  <SelectItem value="employee">{t('role.employee')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddUserDialogOpen(false)} className="text-wp-light/70">{t('common.cancel')}</Button>
            <Button onClick={handleAddUser} disabled={actionLoading || !addUserForm.name || !addUserForm.email || !addUserForm.password} className="bg-wp-medium hover:bg-wp-light text-white">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirm */}
      <Dialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <DialogContent className="max-w-md bg-wp-darker border-wp-dark/50">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2 text-rose-400">
              <Trash2 className="h-5 w-5" />
              {t('superAdmin.removeUser') || 'Delete Employee'}
            </DialogTitle>
            <DialogDescription className="text-wp-light/70">{t('superAdmin.confirmRemoveUser') || 'This action cannot be undone.'}</DialogDescription>
          </DialogHeader>
          <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20">
            <p className="text-white font-medium">{selectedUser?.name}</p>
            <p className="text-wp-light/50 text-sm">{selectedUser?.email}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteUserDialogOpen(false)} className="text-wp-light/70">{t('common.cancel')}</Button>
            <Button onClick={handleDeleteUserConfirm} disabled={actionLoading} className="bg-rose-500 hover:bg-rose-600 text-white">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password */}
      <Dialog open={resetPwdDialogOpen} onOpenChange={setResetPwdDialogOpen}>
        <DialogContent className="max-w-md bg-wp-darker border-wp-dark/50">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Key className="h-5 w-5 text-wp-light" />
              {t('superAdmin.resetPassword')}
            </DialogTitle>
            <DialogDescription className="text-wp-light/50">{selectedUser?.name} ({selectedUser?.email})</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-wp-light/70">{t('superAdmin.newPassword')}</Label>
            <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" className="bg-wp-dark/30 border-wp-dark/50 text-white" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetPwdDialogOpen(false)} className="text-wp-light/70">{t('common.cancel')}</Button>
            <Button onClick={handleResetPassword} disabled={actionLoading || newPassword.length < 6} className="bg-wp-medium hover:bg-wp-light text-white">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function openAddUserDialog(company) {
    setSelectedCompany(company);
    setAddUserForm({ name: '', email: '', password: '', role: 'employee' });
    setAddUserDialogOpen(true);
  }
}
